import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import '../styles/legal.css';

const TERMS_SECTIONS = [
  {
    title: 'Acceptance of Terms',
    body: [
      'By creating an account, logging in, or using LaborConnect, you agree to be bound by these Terms & Conditions (“Terms”). If you do not agree, do not use LaborConnect.',
      'LaborConnect is owned and operated by LaborConnect, Philippines (“LaborConnect,” “we,” “us,” or “our”) as an internal digital portal for members and authorized administrators.',
    ],
  },
  {
    title: 'Purpose of LaborConnect',
    body: [
      'LaborConnect is intended to support legitimate union-related activities, including: Membership registration and profile management; upload and management of required documents; issuance and verification of Digital ID and QR credentials; venue booking requests; and submission and management of support tickets to LaborConnect departments.',
    ],
  },
  {
    title: 'Eligibility and User Accounts',
    body: [
      '3.1 You represent that: You are eligible to be a member of LaborConnect or authorized by LaborConnect to use the portal; information you provide is true, accurate, and complete; and you will promptly update your account details when information changes.',
      '3.2 You are responsible for: Keeping your login credentials confidential; all activities under your account; and immediately reporting suspected unauthorized account use.',
      '3.3 LaborConnect may suspend, restrict, or terminate access for security, policy, legal, or operational reasons.',
    ],
  },
  {
    title: 'Membership Registration and Profile Information',
    body: [
      '4.1 During registration, LaborConnect may collect personal and employment-related information, including: name, contact details, demographic details, and membership details; company/employment details and union role; and dependent information (e.g., number of children, and other necessary dependent data).',
      '4.2 You warrant that any third-party/dependent information submitted is lawful and authorized by the data subject or lawful representative, as required by law.',
      '4.3 Submission of false, misleading, or fraudulent data is prohibited and may result in administrative action, account suspension, and possible legal action.',
    ],
  },
  {
    title: 'File Uploads and Sensitive Content',
    body: [
      '5.1 You may be required to upload documents such as: ID photos, digital signatures, and supporting files for tickets or account validation.',
      '5.2 You must upload only lawful, accurate, and relevant documents. You must not upload malicious files, unauthorized personal data, or content that violates law or third-party rights.',
      '5.3 LaborConnect may review, validate, reject, or request resubmission of uploads for compliance and security purposes.',
    ],
  },
  {
    title: 'Digital ID, QR Code, and QR Reference Number',
    body: [
      '6.1 LaborConnect may generate a Digital ID with a unique QR Code and QR Reference Number to facilitate identity and active membership verification.',
      '6.2 You agree that: the Digital ID and QR credentials are for official LaborConnect purposes only; you will not alter, forge, duplicate, sell, transfer, or misuse your digital credentials; and you will present your QR only to authorized LaborConnect personnel or authorized verifiers.',
      '6.3 Authorized personnel may scan QR codes through device cameras to validate membership status and related verification data.',
      '6.4 Misuse, tampering, or fraudulent use of QR credentials may result in account sanctions and legal action.',
    ],
  },
  {
    title: 'Venue Booking Policies',
    body: [
      '7.1 LaborConnect may allow members to request venue/facility bookings by date and time.',
      '7.2 All bookings are requests only and are subject to LaborConnect approval based on availability, union policies, safety, and administrative considerations.',
      '7.3 Booking status (e.g., Pending, Approved, Rejected, Cancelled) shown in LaborConnect is controlling unless otherwise formally communicated by LaborConnect.',
      '7.4 LaborConnect may modify, cancel, or deny bookings for valid operational, policy, or legal reasons.',
    ],
  },
  {
    title: 'Support Ticketing and Department Handling',
    body: [
      '8.1 Members may submit support requests to relevant departments (e.g., Medical, Legal).',
      '8.2 You must provide respectful, accurate, and lawful information in tickets and attachments.',
      '8.3 LaborConnect may route, reclassify, escalate, or reassign tickets to proper departments for processing.',
    ],
  },
  {
    title: 'AI Assistance Disclaimer',
    body: [
      '9.1 LaborConnect may use an AI feature to generate summaries of member ticket descriptions for administrative efficiency.',
      '9.2 AI summaries are: automated outputs for internal support assistance only; not final determinations, not official LaborConnect decisions, and not legal/medical findings.',
      '9.3 AI-generated summaries do not constitute professional legal advice, medical advice, diagnosis, treatment, or official union ruling. Human review by authorized personnel remains necessary.',
    ],
  },
  {
    title: 'Prohibited Activities',
    body: [
      'You agree not to: use LaborConnect for unlawful, abusive, fraudulent, or harmful acts; impersonate another person or misrepresent affiliation; attempt unauthorized access, scraping, reverse engineering, or disruption of system operations; upload malware, exploit vulnerabilities, or interfere with security; use LaborConnect data for unauthorized commercial or political purposes; and violate privacy/data protection rights of others.',
    ],
  },
  {
    title: 'System Availability and Changes',
    body: [
      'LaborConnect may update, modify, suspend, or discontinue all or part of LaborConnect at any time for maintenance, security, legal, or operational reasons, with reasonable notice when practicable.',
    ],
  },
  {
    title: 'Intellectual Property',
    body: [
      'LaborConnect content, branding, and system materials are owned by LaborConnect or licensed to LaborConnect. No ownership rights are transferred to users by using the portal.',
    ],
  },
  {
    title: 'Limitation of Liability',
    body: [
      'To the extent allowed by law: LaborConnect is provided on an “as available” basis; LaborConnect is not liable for indirect, incidental, special, or consequential damages arising from portal use, outages, delays, unauthorized access, or user-submitted inaccuracies; and LaborConnect’s liability, when legally established, is limited to what Philippine law requires under the circumstances.',
      'Nothing in these Terms excludes liability that cannot be excluded under applicable law.',
    ],
  },
  {
    title: 'Indemnity',
    body: [
      'You agree to indemnify and hold LaborConnect, its officers, employees, and authorized representatives harmless from claims, losses, and liabilities arising from your breach of these Terms or unlawful use of LaborConnect.',
    ],
  },
  {
    title: 'Privacy and Data Protection',
    body: [
      'Your use of LaborConnect is also governed by the LaborConnect Data Privacy Policy, which forms an integral part of these Terms.',
    ],
  },
  {
    title: 'Governing Law and Venue',
    body: [
      'These Terms are governed by Philippine law. Any dispute shall be subject to the appropriate courts in the Philippines, without prejudice to rights and remedies under special laws and regulatory processes.',
    ],
  },
  {
    title: 'Changes to Terms',
    body: [
      'LaborConnect may amend these Terms. Material changes will be posted in LaborConnect with an updated effective date. Continued use after effectivity constitutes acceptance.',
    ],
  },
  {
    title: 'Contact',
    body: [
      'For concerns about these Terms, contact: [Insert LaborConnect Office / Legal Contact Email / Phone]',
    ],
  },
];

export default function TermsAndConditionsPage() {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    const hasStoredAuth = Boolean(
      localStorage.getItem('alu_user_auth') ||
      sessionStorage.getItem('alu_user_auth_session') ||
      localStorage.getItem('alu_user') ||
      localStorage.getItem('alu_token')
    );

    navigate(hasStoredAuth ? '/dashboard' : '/');
  };

  return (
    <main className="legal-page">
      <div className="legal-page__container">
        <button type="button" className="legal-page__back-link" onClick={handleBack}>
          <ArrowLeft size={16} />
          Back to LaborConnect
        </button>

        <header className="legal-page__header">
          <div className="legal-page__icon">
            <ShieldCheck size={24} />
          </div>
          <div>
            <h1>LaborConnect Terms &amp; Conditions</h1>
            <p>
              Effective Date: Dec 2026 | Last Updated: Mar 2026
            </p>
          </div>
        </header>

        <section className="legal-page__notice">
          <strong>Important:</strong> This portal is for authorized union-related use. By using LaborConnect, you agree to these legally binding terms.
        </section>

        <div className="legal-page__content">
          {TERMS_SECTIONS.map((section) => (
            <section key={section.title} className="legal-page__section">
              <h2>{section.title}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}
        </div>

        <footer className="legal-page__footer">
          See also: <Link to="/privacy-policy" className="legal-page__inline-link">LaborConnect Data Privacy Policy</Link>
        </footer>
      </div>
    </main>
  );
}
